import React, { useState, useEffect } from 'react';
import styled from "@emotion/styled";
import { Button, Card, CardContent, Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper, Pagination, CircularProgress, Chip, Popover, Tooltip, Typography, TextField, InputAdornment, IconButton, ListItemText, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Radio, RadioGroup, FormControlLabel, useMediaQuery } from "@mui/material";
import { Download, Search, Clear, Send, Warning, InfoOutlined, Description, HourglassEmpty, AttachMoney } from "@mui/icons-material";
import { TabPanel } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInvoices, exportInvoicesIif, sendAllDraftInvoices } from '../redux/ducks/invoices';
import { downloadBlob } from '../utils/downloadUtils';
import { statusColor } from '../utils/statusColors';
import AppDatePicker from './AppDatePicker';
import StatCard from './StatCard';
import { formatDate, formatDayShort, formatMonthKey } from '../utils/dateUtils';

const StatsGrid = styled(Grid)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SendScopeGroup = styled(RadioGroup)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const SendMonthRow = styled(Box)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(1),
}));

const currentMonthStartDayjs = () => dayjs().startOf('month');
const currentMonthEndDayjs = () => dayjs().endOf('month');

const money = (n) => `$ ${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const invoiceCount = (n) => `${n || 0} ${n === 1 ? 'invoice' : 'invoices'}`;

const InvoiceNumberLink = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    cursor: "pointer",
    color: theme.palette.primary.main,
    display: "inline-block",
    "&:hover": {
        color: theme.palette.primary.dark,
        textDecoration: "underline",
    },
}));

const InvoicesTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    paddingBottom: 0,
}));

const SectionHeaderText = styled(Box)({
    flex: '1 1 auto',
    minWidth: 0,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    overflowWrap: 'break-word',
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.up('sm')]: {
        gap: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        '& > *': {
            flex: '1 1 0',
        },
    },
}));

// A disabled MUI Button fires no hover events, so the Tooltip needs this non-disabled wrapper to stay visible mid-export.
const ExportButtonWrap = styled('span')(({ theme }) => ({
    display: 'inline-flex',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        '& > *': {
            width: '100%',
        },
    },
}));

const ExportPanel = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    width: 320,
    maxWidth: '100%',
}));

const ExportTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
}));

const ExportHelper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
}));

const ExportRangeRow = styled('div')(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const ExportActions = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
}));

const MobileCardList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const InvoiceRowCard = styled(Paper, { shouldForwardProp: (prop) => prop !== 'isCredit' })(({ theme, isCredit }) => ({
    padding: theme.spacing(1.5),
    backgroundColor: isCredit ? theme.palette.action.hover : theme.palette.background.paper,
    cursor: 'pointer',
    transition: theme.transitions.create('box-shadow', { duration: theme.transitions.duration.short }),
    '&:hover': {
        boxShadow: theme.shadows[3],
    },
}));

const CardTopRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
}));

const CardClient = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    overflowWrap: 'break-word',
}));

const CardMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
}));

const CardAmount = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const CardChips = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

const EmptyMobileState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4, 2),
    textAlign: 'center',
}));

const SearchRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const InvoiceTableRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== 'isCredit' })(({ theme, isCredit }) => ({
    backgroundColor: isCredit ? theme.palette.action.hover : 'inherit',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const RowChip = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(1),
}));

const NoteChip = styled(Chip)(({ theme }) => ({
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    '& .MuiChip-icon': {
        color: theme.palette.text.secondary,
    },
}));

const RowNoteChip = styled(NoteChip)(({ theme }) => ({
    marginLeft: theme.spacing(1),
}));

const ReasonList = styled('ul')(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(2),
}));

const StatusCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const StatusDot = styled(Box, { shouldForwardProp: (prop) => prop !== 'status' })(({ theme, status }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: statusColor(theme, 'invoice', status),
}));

const EmptyTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const PaginationRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
}));

const DEFAULT_WARNING_REASON = {
    warn: 'Generated with orphan records — review on the invoice page.',
    info: 'This invoice has a note worth checking on the invoice page.',
};

const InvoiceWarningChip = ({ invoice, inline }) => {
    const level = invoice.warningLevel || (invoice.hasOrphans ? 'warn' : 'none');
    if (level !== 'warn' && level !== 'info') return null;

    const reasons = (invoice.warningReasons || []).filter(Boolean);
    const title = reasons.length > 1
      ? <ReasonList>{reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ReasonList>
      : (reasons[0] || DEFAULT_WARNING_REASON[level]);

    if (level === 'warn') {
      const WarnChip = inline ? RowChip : Chip;
      return (
        <Tooltip title={title}>
          <WarnChip icon={<Warning fontSize="small" />} label="Review" size="small" color="warning" />
        </Tooltip>
      );
    }

    const InfoChip = inline ? RowNoteChip : NoteChip;
    return (
      <Tooltip title={title}>
        <InfoChip icon={<InfoOutlined fontSize="small" />} label="Note" size="small" variant="outlined" />
      </Tooltip>
    );
};

const BillingInvoices = ({ tabValue }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [invoices, setInvoices] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [exportMonth, setExportMonth] = useState(dayjs().subtract(1, 'month').startOf('month'));
    const [exportUseRange, setExportUseRange] = useState(false);
    const [exportFrom, setExportFrom] = useState('');
    const [exportTo, setExportTo] = useState('');
    const [isSendingAll, setIsSendingAll] = useState(false);
    const [confirmSendAllOpen, setConfirmSendAllOpen] = useState(false);
    const [sendScope, setSendScope] = useState('month');
    const [sendMonth, setSendMonth] = useState(dayjs().subtract(1, 'month').startOf('month'));
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('invoiceDate');
    const [sortDir, setSortDir] = useState('desc');
    const limit = 50;

    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (tabValue === 'invoices') {
            loadInvoices();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, page, search, sortBy, sortDir]);

    const loadInvoices = () => {
        setIsLoading(true);
        const start = (page - 1) * limit;
        dispatch(getInvoices(start, limit, null, (response) => {
            setIsLoading(false);
            if (response && response.invoices) {
                setInvoices(response.invoices);
                setTotal(response.total);
                setStats(response.stats || null);
            }
        }, { search, sortBy, sortDir }));
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir('asc');
        }
        setPage(1);
    };

    const sortLabelProps = (field) => ({
        active: sortBy === field,
        direction: sortBy === field ? sortDir : 'asc',
        onClick: () => handleSort(field),
    });

    const exportBounds = { month: currentMonthStartDayjs(), day: currentMonthEndDayjs() };
    const isExportMonthValid = dayjs.isDayjs(exportMonth) && exportMonth.isValid() && !exportMonth.startOf('month').isAfter(exportBounds.month);
    const isExportRangeValid = Boolean(exportFrom) && Boolean(exportTo) && exportFrom <= exportTo && exportTo <= exportBounds.day.format('YYYY-MM-DD');
    const canExport = exportUseRange ? isExportRangeValid : isExportMonthValid;

    const runExport = (opts, suffix) => {
        setExportAnchorEl(null);
        setIsExporting(true);
        dispatch(exportInvoicesIif(opts, (data) => {
            setIsExporting(false);
            if (data) {
                downloadBlob(data, `quickbooks-invoices-${suffix}.iif`, 'application/octet-stream');
            }
        }));
    };

    const handleExport = () => {
        if (!canExport) return;
        if (exportUseRange) {
            runExport({ start: exportFrom, end: exportTo }, `${exportFrom}_${exportTo}`);
            return;
        }
        const from = exportMonth.startOf('month');
        const to = exportMonth.endOf('month');
        runExport({ start: from.format('YYYY-MM-DD'), end: to.format('YYYY-MM-DD') }, from.format('YYYY-MM'));
    };

    const statFor = (status) => stats?.byStatus?.[status] || { count: 0, amount: 0, outstanding: 0 };
    const draftCount = statFor('Draft').count;

    const isSendMonthValid = dayjs.isDayjs(sendMonth) && sendMonth.isValid();
    const canSendDrafts = sendScope === 'all' || isSendMonthValid;
    const sendMonthLabel = isSendMonthValid ? sendMonth.format('MMMM YYYY') : '';

    const handleSendAllDrafts = () => {
        if (!canSendDrafts) return;
        const opts = sendScope === 'month' ? { month: sendMonth.format('YYYY-MM') } : {};
        setIsSendingAll(true);
        setConfirmSendAllOpen(false);
        dispatch(sendAllDraftInvoices(opts, () => {
            setIsSendingAll(false);
            loadInvoices();
        }));
    };

    const formatBillingPeriod = (startDate, endDate) => {
        const day = formatDate(endDate || startDate);
        return day ? formatMonthKey(`${day.slice(0, 4)}${day.slice(5, 7)}`) : '';
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    return (
      <InvoicesTabPanel value="invoices">
        <Card>
          <SectionHeader>
            <SectionHeaderText>
              <SectionTitle variant="h6">Invoice Management</SectionTitle>
              <SectionSubtitle variant="body2">Generate, review, and manage client invoices</SectionSubtitle>
            </SectionHeaderText>
            <HeaderActions>
              <Button
                variant="contained"
                startIcon={isSendingAll ? <CircularProgress size={16} /> : <Send />}
                onClick={() => setConfirmSendAllOpen(true)}
                disabled={isSendingAll}
              >
                {draftCount > 0 ? `Send Drafts (${draftCount})` : 'Send Drafts'}
              </Button>
              <Tooltip title="Downloads a QuickBooks .iif file of the invoices you pick — one month, a date range, or everything still in Draft — with their line items, ready to import into QuickBooks.">
                <ExportButtonWrap>
                  <Button
                    variant="outlined"
                    startIcon={isExporting ? <CircularProgress size={16} /> : <Download />}
                    onClick={(event) => setExportAnchorEl(event.currentTarget)}
                    disabled={isExporting}
                  >
                    Download QuickBooks IIF File
                  </Button>
                </ExportButtonWrap>
              </Tooltip>
            </HeaderActions>
          </SectionHeader>
          <CardContent>
            <StatsGrid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  size="large"
                  icon={<Description fontSize="small" />}
                  label="Draft"
                  value={money(statFor('Draft').amount)}
                  sub={`${invoiceCount(statFor('Draft').count)} not sent yet`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  size="large"
                  icon={<HourglassEmpty fontSize="small" />}
                  label="Open"
                  value={money(statFor('Open').amount)}
                  sub={`${invoiceCount(statFor('Open').count)} awaiting payment`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  size="large"
                  tone={statFor('Overdue').count > 0 ? 'error' : 'neutral'}
                  icon={<Warning fontSize="small" />}
                  label="Overdue"
                  value={money(statFor('Overdue').amount)}
                  sub={`${invoiceCount(statFor('Overdue').count)} past due`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  size="large"
                  icon={<AttachMoney fontSize="small" />}
                  label="Still Owed"
                  value={money(stats?.outstanding)}
                  sub={search ? 'Across invoices matching your search' : 'Open and overdue, less payments received'}
                />
              </Grid>
            </StatsGrid>
            <SearchRow>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by client name or invoice number"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchInput('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </SearchRow>
            {isLoading ? (
              <CenteredLoading>
                <CircularProgress />
              </CenteredLoading>
            ) : isMobile ? (
              <>
                {invoices.length > 0 ? (
                  <MobileCardList>
                    {invoices.map((invoice) => (
                      <InvoiceRowCard
                        key={invoice._id}
                        variant="outlined"
                        isCredit={invoice.isCredit}
                        onClick={() => navigate(`/billing/invoices/${invoice._id}`)}
                      >
                        <CardTopRow>
                          <InvoiceNumberLink variant="body1" component="span">
                            {invoice.invoiceNumber}
                          </InvoiceNumberLink>
                          <CardChips>
                            {invoice.isCredit && <Chip label="Credit" size="small" color="secondary" />}
                            <InvoiceWarningChip invoice={invoice} />
                            <StatusCell>
                              <StatusDot status={invoice.status} />
                              <Typography variant="caption">{invoice.status}</Typography>
                            </StatusCell>
                          </CardChips>
                        </CardTopRow>
                        <CardClient variant="body2">
                          {invoice.account?.name || invoice.client?.name || 'Unknown'}
                        </CardClient>
                        {invoice.account?.name && invoice.client?.name && (
                          <Typography variant="caption" color="text.secondary">
                            {invoice.client.name}
                          </Typography>
                        )}
                        <CardMetaRow>
                          <Typography variant="caption" color="text.secondary">
                            {formatBillingPeriod(invoice.billingPeriodStart, invoice.billingPeriodEnd)}
                          </Typography>
                          <CardAmount variant="body2">
                            ${(invoice.amount || 0).toFixed(2)} {invoice.currency || 'USD'}
                          </CardAmount>
                        </CardMetaRow>
                      </InvoiceRowCard>
                    ))}
                  </MobileCardList>
                ) : (
                  <EmptyMobileState>
                    <Typography variant="body2" color="text.secondary">No invoices found</Typography>
                  </EmptyMobileState>
                )}
                {total > limit && (
                  <PaginationRow>
                    <Pagination
                      count={Math.ceil(total / limit)}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </PaginationRow>
                )}
              </>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={sortBy === 'invoiceNumber' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('invoiceNumber')}>Invoice #</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'client' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('client')}>Client</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'invoiceDate' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('invoiceDate')}>Date</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'sentDate' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('sentDate')}>Sent Date</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'paidDate' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('paidDate')}>Paid Date</TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={sortBy === 'amount' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('amount')}>Amount</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                          <TableSortLabel {...sortLabelProps('status')}>Status</TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length > 0 ? (
                        invoices.map((invoice) => (
                          <InvoiceTableRow key={invoice._id} isCredit={invoice.isCredit}>
                            <TableCell>
                              <InvoiceNumberLink
                                variant="body2"
                                component="span"
                                onClick={() => navigate(`/billing/invoices/${invoice._id}`)}
                              >
                                {invoice.invoiceNumber}
                              </InvoiceNumberLink>
                              {invoice.isCredit && (
                                <RowChip label="Credit" size="small" color="secondary" />
                              )}
                              <InvoiceWarningChip invoice={invoice} inline />
                            </TableCell>
                            <TableCell>
                              <ListItemText
                                disableTypography
                                primary={
                                  <Typography variant="body2">
                                    {invoice.account?.name || invoice.client?.name || 'Unknown'}
                                  </Typography>
                                }
                                secondary={
                                  invoice.account?.name && invoice.client?.name
                                    ? <Typography variant="caption" color="text.secondary">{invoice.client.name}</Typography>
                                    : null
                                }
                              />
                            </TableCell>
                            <TableCell>{formatBillingPeriod(invoice.billingPeriodStart, invoice.billingPeriodEnd)}</TableCell>
                            <TableCell>{formatDayShort(invoice.sentDate)}</TableCell>
                            <TableCell>{formatDayShort(invoice.paidDate)}</TableCell>
                            <TableCell align="right">
                              ${(invoice.amount || 0).toFixed(2)} {invoice.currency || 'USD'}
                            </TableCell>
                            <TableCell>
                              <StatusCell>
                                <StatusDot status={invoice.status} />
                                {invoice.status}
                              </StatusCell>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                onClick={() => navigate(`/billing/invoices/${invoice._id}`)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </InvoiceTableRow>
                        ))
                      ) : (
                        <TableRow>
                          <EmptyTableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No invoices found
                            </Typography>
                          </EmptyTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {total > limit && (
                  <PaginationRow>
                    <Pagination
                      count={Math.ceil(total / limit)}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </PaginationRow>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <Dialog open={confirmSendAllOpen} onClose={() => setConfirmSendAllOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send draft invoices</DialogTitle>
          <DialogContent>
            <DialogContentText>
              The drafts you pick will be marked Open, their time records / charges synced to that status, and an email with the invoice PDF sent to each client's billing contact. Drafts whose client has no billing contact configured will be marked Open but the email will be skipped.
            </DialogContentText>
            <SendScopeGroup value={sendScope} onChange={(event) => setSendScope(event.target.value)}>
              <FormControlLabel value="month" control={<Radio />} label="Only drafts dated in one month" />
              <SendMonthRow>
                <DatePicker
                  label="Month"
                  views={['year', 'month']}
                  openTo="month"
                  format="MMMM YYYY"
                  maxDate={currentMonthStartDayjs()}
                  value={sendMonth}
                  onChange={(value) => setSendMonth(value)}
                  disabled={sendScope !== 'month'}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </SendMonthRow>
              <FormControlLabel value="all" control={<Radio />} label="Every draft invoice, whatever its date" />
            </SendScopeGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmSendAllOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSendAllDrafts} disabled={!canSendDrafts}>
              {sendScope === 'month' ? `Send ${sendMonthLabel || 'Month'}` : 'Send Every Draft'}
            </Button>
          </DialogActions>
        </Dialog>
        <Popover
          open={Boolean(exportAnchorEl)}
          anchorEl={exportAnchorEl}
          onClose={() => setExportAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <ExportPanel>
            <ExportTitle variant="subtitle1" component="h2">Export invoices to QuickBooks</ExportTitle>
            <ExportHelper variant="body2">
              Invoices are dated at the end of the period they cover, so a month gives you that month&apos;s billing.
              A month or range includes invoices already sent or paid, not only drafts.
            </ExportHelper>
            {exportUseRange ? (
              <ExportRangeRow>
                <AppDatePicker label="From" value={exportFrom} onChange={(value) => setExportFrom(value)} size="small" maxDate={exportBounds.day} />
                <AppDatePicker label="To" value={exportTo} onChange={(value) => setExportTo(value)} size="small" minDate={exportFrom ? dayjs(exportFrom) : undefined} maxDate={exportBounds.day} />
              </ExportRangeRow>
            ) : (
              <DatePicker
                label="Month"
                views={['year', 'month']}
                openTo="month"
                format="MMMM YYYY"
                maxDate={exportBounds.month}
                value={exportMonth}
                onChange={(value) => setExportMonth(value)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            )}
            <Button size="small" onClick={() => setExportUseRange(!exportUseRange)}>
              {exportUseRange ? 'Pick a month instead' : 'Use a custom date range instead'}
            </Button>
            <ExportActions>
              <Tooltip title="Every invoice still in Draft, whatever month it covers">
                <Button size="small" color="inherit" onClick={() => runExport({}, 'drafts')}>All drafts</Button>
              </Tooltip>
              <Button variant="contained" onClick={handleExport} disabled={!canExport}>Download IIF</Button>
            </ExportActions>
          </ExportPanel>
        </Popover>
      </InvoicesTabPanel>
    );
}

export default BillingInvoices;
